import { useEffect, useState } from 'react';
import { useNavigate } from '../routing';
import EmptyState from './EmptyState';
import HeartIcon from './HeartIcon';
import { analysisApi, classificationsApi, formatCurrency } from '../utils/api';

// The "no bars to draw" copy for the by-classification views — the card on /analysis
// and the full report. The sibling of SupplierSpendEmptyState, and for the same
// reason: two screens, one voice.
//
// But this axis has states the supplier axis cannot have, and conflating them
// is the bug this component exists to fix. Every document has a supplier, so
// "no suppliers" only ever means "no documents". A classification has to be CREATED
// and then a product TAGGED into it, so spend can exist in full while the
// breakdown is empty — and that is not emptiness, it is unfinished setup with a
// different action attached.
//
// Before this, all of these rendered "אין נתונים בטווח הנבחר", which is false
// in four of the six cases below and actionable in none of them.
//
//   no-match       a filter matched nothing. The period HAS spend.
//   empty-period   the account has purchases, just not in this period.
//   new-account    no purchase data at all. A welcome, not a failure.
//   no-classifications  spend exists; nobody has created a classification yet.
//   nothing-tagged spend exists, classifications exist, no product is tagged.
//   net-zero       credits in the window cancel the purchases out.
//
// Copy is gender-neutral (impersonal/infinitive) per the house style.

export default function ClassificationSpendEmptyState({
  period,
  filterActive = false,
  total = 0,
  untaggedSpend = 0,
  // Whether the server returned ANY classification rows. Distinguishes "no documents
  // in this window" from "documents exist but credits cancelled them out" —
  // the second has rows that happen to net to zero, and telling a user their
  // period is empty when it holds a month of invoices and returns is wrong.
  hasRows = false,
  canEdit = false,
  // Passed in rather than read from AuthContext: this leaf renders inside two
  // different parents that both already hold the auth value, and a context read
  // here is a second, redundant coupling.
  isAdmin = false,
}) {
  const navigate = useNavigate();

  //   null = still asking · true = has history · false = brand new
  const [everHadPurchases, setEverHadPurchases] = useState(null);
  // Only asked when spend exists but nothing is tagged, to tell "no classifications
  // yet" from "classifications exist, nothing filed into them" — two different
  // sentences with two different buttons.
  //   null = unknown/not asked · true/false = answered
  const [hasClassifications, setHasClassifications] = useState(null);

  // There IS spend in this window; the breakdown is empty for a setup reason,
  // not a data one.
  const spendExists = total > 0;

  useEffect(() => {
    // A filter miss says nothing about the account, so don't spend a request.
    if (filterActive || spendExists) return;
    let cancelled = false;
    analysisApi.hasHistory()
      .then(r => { if (!cancelled) setEverHadPurchases(Boolean(r.data?.has_history)); })
      // On failure assume there IS history: greeting an established customer
      // as brand new is the worse of the two mistakes.
      .catch(() => { if (!cancelled) setEverHadPurchases(true); })
    return () => { cancelled = true; };
  }, [filterActive, spendExists]);

  useEffect(() => {
    if (!spendExists || filterActive) return;
    let cancelled = false;
    classificationsApi.getAll()
      .then(r => { if (!cancelled) setHasClassifications((r.data || []).length > 0); })
      // On failure assume classifications exist: "שייך פריטים" still leads somewhere
      // useful, while "הגדרת סיווגים" would be wrong for anyone who has them.
      .catch(() => { if (!cancelled) setHasClassifications(true); })
    return () => { cancelled = true; };
  }, [spendExists, filterActive]);

  if (filterActive) {
    return (
      <EmptyState compact message={`לא נמצא רכש ב${period} לפי הסינון שנבחר`}>
        <p className="empty-subline">אפשר לנקות את החיפוש או לבחור תקופה אחרת.</p>
      </EmptyState>
    );
  }

  // Rows came back, but they net to nothing: credits in the window cancel the
  // purchases out. Not a tagging problem, and the tagging CTA would be a
  // non-sequitur, so it gets its own words.
  if (hasRows && total <= 0) {
    return (
      <EmptyState compact message={`אין רכש נטו ב${period}`}>
        <p className="empty-subline">
          הזיכויים בתקופה מבטלים את הרכש. אפשר לבחור תקופה ארוכה יותר כדי לראות את התמונה המלאה.
        </p>
      </EmptyState>
    );
  }

  // ── spend exists, so this is setup, not emptiness ──────────────────────────
  if (spendExists) {
    // Everything that could be tagged already is; what is left is untaggable.
    // Rare, but it must not render the "tag your products" plea.
    if (untaggedSpend <= 0) {
      return (
        <EmptyState compact message={`אין רכש מסווג להצגה ב${period}`}>
          <p className="empty-subline">
            הרכש בתקופה אינו ניתן לשיוך לסיווג — שורות בחשבונית שלא נמצא להן מוצר תואם בקטלוג.
          </p>
        </EmptyState>
      );
    }

    if (hasClassifications === false) {
      // The mermaid, not the compact magnifier: spend already exists here, so
      // this reads as an invitation to set something up rather than "your
      // search matched nothing" — the same weight the brand-new-account state
      // below carries. The heart on her shoulder is purely decorative (the
      // same touch as the login footer's "Made with ♥"), so it stays
      // aria-hidden rather than competing with the copy for a screen reader.
      return (
        <EmptyState
          message="עדיין לא הוגדרו סיווג פריטים"
          accent={<HeartIcon size={14} style={{ position: 'absolute', insetInlineEnd: -2, bottom: 8 }} />}
        >
          <p className="empty-subline">
            יש כאן {formatCurrency(total)} של רכש שרק מחכה להתארגן. סיווגים -
            כמו בשר, ירקות או ניקיון - הן הדרך הכי פשוטה להבין לאן הולך הכסף.
          </p>
          {canEdit && (
            <button type="button" className="empty-action" onClick={() => navigate('/item-classifications')}>
              להגדרת סיווגים
            </button>
          )}
        </EmptyState>
      );
    }

    return (
      <EmptyState compact message="אף מוצר עדיין לא שויך לסיווג">
        <p className="empty-subline">
          {formatCurrency(untaggedSpend)} ב{period} רשומים תחת ״לא מסווג״.
          שיוך המוצרים לסיווגים יפתח כאן את הפילוח.
        </p>
        {canEdit && (
          <button type="button" className="empty-action" onClick={() => navigate('/item-classifications?untagged=1')}>
            שייך פריטים
          </button>
        )}
      </EmptyState>
    );
  }

  // ── no spend at all in this window ────────────────────────────────────────
  // Until the probe answers, use the period wording. It is the common case and
  // never over-claims — worst case a brand-new account sees it for a moment.
  if (everHadPurchases !== false) {
    return (
      <EmptyState compact message={`אין נתוני רכש ב${period}`}>
        {/* Word-for-word the supplier card's line. Same fact, same words: two
            cards on one screen describing one situation differently reads as
            two different problems. */}
        <p className="empty-subline">אפשר לדלג לתקופה אחרת כדי לראות רכש קודם.</p>
      </EmptyState>
    );
  }

  return (
    <EmptyState message="כאן יופיע הרכש לפי סיווג פריטים">
      <p className="empty-subline">
        ברגע שייקלטו המסמכים הראשונים ויוגדרו סיווגים, אפשר יהיה לראות כאן על מה
        מוציאים הכי הרבה — וכמה מכל סיווג.
      </p>
      {isAdmin && (
        <button type="button" className="empty-action" onClick={() => navigate('/upload')}>
          להעלאת המסמך הראשון
        </button>
      )}
    </EmptyState>
  );
}
